import { useState, useMemo, useCallback } from 'react';
import { useWriteContract, useWaitForTransactionReceipt, useReadContract, useReadContracts, useAccount, useChainId } from 'wagmi';
import { optimism } from 'wagmi/chains';
import { parseUnits, formatUnits, isAddress } from 'viem';
import { COHORT_ABI, ERC20_ABI } from '../contract/abi';
import { DistributionPreview, useDistribution } from './DistributionPreview';
import { ConnectWallet } from './ConnectWallet';
import { AddressInput } from './AddressInput';

interface DistributionViewProps {
  cohortId: `0x${string}`;
  cohortTitle: string;
  contractAddress: `0x${string}`;
  multipliers: number[];
  onBack: () => void;
  onMultipliersChange: (multipliers: number[]) => void;
}

export function DistributionView({
  cohortId,
  contractAddress,
  multipliers,
  onBack,
  onMultipliersChange,
}: DistributionViewProps) {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const isCorrectChain = chainId === optimism.id;
  const canTransact = isConnected && isCorrectChain;

  const [tokenAddress, setTokenAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [addresses, setAddresses] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'input' | 'approve' | 'distribute'>('input');

  const { writeContract: writeApprove, data: approveHash, isPending: isApprovePending } = useWriteContract();
  const { writeContract: writeDistribute, data: distributeHash, isPending: isDistributePending } = useWriteContract();

  const { isLoading: isApproveConfirming, isSuccess: isApproveSuccess } =
    useWaitForTransactionReceipt({ hash: approveHash });
  const { isLoading: isDistributeConfirming, isSuccess: isDistributeSuccess } =
    useWaitForTransactionReceipt({ hash: distributeHash });

  // Read token info
  const { data: tokenDecimals } = useReadContract({
    address: tokenAddress as `0x${string}`,
    abi: ERC20_ABI,
    functionName: 'decimals',
    query: { enabled: isAddress(tokenAddress) },
  });

  const { data: tokenSymbol } = useReadContract({
    address: tokenAddress as `0x${string}`,
    abi: ERC20_ABI,
    functionName: 'symbol',
    query: { enabled: isAddress(tokenAddress) },
  });

  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: tokenAddress as `0x${string}`,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: address && isAddress(tokenAddress) ? [address, contractAddress] : undefined,
    query: { enabled: !!address && isAddress(tokenAddress) },
  });

  // Get participant IDs from chain
  const { data: participantIds } = useReadContract({
    address: contractAddress,
    abi: COHORT_ABI,
    functionName: 'getParticipantIds',
    args: [cohortId],
  });

  // Build contract read configs for participant names (look up from on-chain registry)
  const participantNameConfigs = useMemo(() => {
    if (!participantIds || participantIds.length === 0) return [];
    return participantIds.map((id) => ({
      address: contractAddress,
      abi: COHORT_ABI,
      functionName: 'names' as const,
      args: [id] as const,
    }));
  }, [participantIds, contractAddress]);

  // Fetch participant names from chain
  const { data: participantNameResults } = useReadContracts({
    contracts: participantNameConfigs,
  });

  // Extract participant names from results
  const participantNames = useMemo(() => {
    if (!participantNameResults || !participantIds) return [];
    return participantNameResults.map((result, index) => {
      const name = result?.result as string | undefined;
      return name || `Participant ${index + 1}`;
    });
  }, [participantNameResults, participantIds]);


  // Use shared distribution hook
  const { distribution, scaledMultipliers } = useDistribution(
    cohortId,
    contractAddress,
    multipliers,
    participantNames
  );

  const updateAddress = useCallback((index: number, addr: string) => {
    setAddresses(prev => {
      const newAddresses = new Map(prev);
      newAddresses.set(`participant_${index}`, addr);
      return newAddresses;
    });
  }, []);

  const validateInputs = (): boolean => {
    setError(null);

    if (!isAddress(tokenAddress)) {
      setError('Invalid token address');
      return false;
    }

    if (!amount || parseFloat(amount) <= 0) {
      setError('Invalid amount');
      return false;
    }

    const participantCount = participantIds?.length || 0;
    for (let i = 0; i < participantCount; i++) {
      const addr = addresses.get(`participant_${i}`);
      if (!addr || !isAddress(addr)) {
        setError(`Invalid address for participant ${i + 1}`);
        return false;
      }
    }

    return true;
  };

  const handleApprove = async () => {
    if (!validateInputs() || !tokenDecimals) return;

    try {
      const amountBigInt = parseUnits(amount, tokenDecimals);

      writeApprove({
        address: tokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: 'approve',
        args: [contractAddress, amountBigInt],
      });

      setStep('approve');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approval failed');
    }
  };

  const handleDistribute = async () => {
    if (!validateInputs() || !tokenDecimals) return;

    try {
      const amountBigInt = parseUnits(amount, tokenDecimals);

      // Prepare arrays in order
      const addressArray: `0x${string}`[] = [];
      const participantCount = participantIds?.length || 0;

      for (let i = 0; i < participantCount; i++) {
        const addr = addresses.get(`participant_${i}`);
        if (addr && isAddress(addr)) {
          addressArray.push(addr as `0x${string}`);
        }
      }

      writeDistribute({
        address: contractAddress,
        abi: COHORT_ABI,
        functionName: 'distribute',
        args: [
          cohortId,
          tokenAddress as `0x${string}`,
          amountBigInt,
          addressArray,
          scaledMultipliers,
        ],
      });

      setStep('distribute');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Distribution failed');
    }
  };

  // Auto-refetch allowance after approval
  if (isApproveSuccess && step === 'approve') {
    refetchAllowance();
  }

  if (isDistributeSuccess) {
    return (
      <div className="container">
        <div className="card">
          <div className="message message-success">
            Tokens distributed successfully!
          </div>
          <button onClick={onBack} className="btn-primary">
            Back to Cohort
          </button>
        </div>
      </div>
    );
  }

  const decimals = tokenDecimals || 18;
  const amountBigInt = amount ? parseUnits(amount, decimals) : 0n;
  const hasAllowance = allowance && allowance >= amountBigInt;

  const participantCount = participantIds?.length || 0;
  const allAddressesValid = Array.from({ length: participantCount }).every((_, i) => {
    const addr = addresses.get(`participant_${i}`);
    return addr && isAddress(addr);
  });
  const canDistribute = canTransact && isAddress(tokenAddress) && amount && parseFloat(amount) > 0 && allAddressesValid && hasAllowance;

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">
          <h1>Distribute Tokens</h1>
          <button onClick={onBack} className="btn-secondary">
            Back
          </button>
        </div>

        {error && (
          <div className="message message-error">
            {error}
          </div>
        )}

        {distribution.length > 0 ? (
          <DistributionPreview
            cohortId={cohortId}
            contractAddress={contractAddress}
            participantNames={participantNames}
            multipliers={multipliers}
            onMultipliersChange={onMultipliersChange}
          />
        ) : (
          <div className="distribution-preview">
            <h3>Distribution Preview</h3>
            <p className="text-muted">No distribution data available.</p>
          </div>
        )}

        <div className="form-group">
          <AddressInput
            label="Token Address"
            onAddressChange={(addr) => setTokenAddress(addr || '')}
            placeholder="0x... or token.eth"
            id="token-address"
          />
          {tokenSymbol && (
            <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: 'var(--space-xs)' }}>
              Token: {tokenSymbol} (decimals: {decimals})
            </p>
          )}
        </div>

        <div className="form-group">
          <label>Amount</label>
          <input
            type="text"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.0"
          />
        </div>

        <div className="form-group">
          <label style={{ marginBottom: 'var(--space-md)' }}>Participant Addresses</label>
          {Array.from({ length: participantCount }).map((_, index) => (
            <AddressInput
              key={index}
              label={participantNames[index] || `Participant ${index + 1}`}
              onAddressChange={(addr) => updateAddress(index, addr || '')}
              placeholder="0x... or name.eth"
              id={`participant-address-${index}`}
            />
          ))}
        </div>

        {canTransact && allowance !== undefined && (
          <div className={`message ${hasAllowance ? 'message-success' : 'message-info'}`}>
            {hasAllowance
              ? `Token approval sufficient (${formatUnits(allowance, decimals)} ${tokenSymbol || ''})`
              : `Current allowance: ${formatUnits(allowance, decimals)} ${tokenSymbol || ''}`}
          </div>
        )}

        <ConnectWallet />

        {canTransact && (
          <div className="flex gap-md">
            {!hasAllowance && (
              <button
                onClick={handleApprove}
                disabled={isApprovePending || isApproveConfirming || !tokenAddress || !amount}
                className="btn-primary"
              >
                {isApprovePending || isApproveConfirming ? (
                  <>
                    <span className="spinner" />
                    {isApprovePending ? 'Confirm in wallet...' : 'Approving...'}
                  </>
                ) : (
                  'Approve Token'
                )}
              </button>
            )}

            <button
              onClick={handleDistribute}
              disabled={!canDistribute || isDistributePending || isDistributeConfirming}
              className="btn-primary"
            >
              {isDistributePending || isDistributeConfirming ? (
                <>
                  <span className="spinner" />
                  {isDistributePending ? 'Confirm in wallet...' : 'Distributing...'}
                </>
              ) : (
                'Distribute Tokens'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
